<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { ElMessage } from "element-plus";
import { storeToRefs } from "pinia";
import { useLayoutStore } from "@/stores/layout";
import { useUserStore } from "@/stores/user";
import {
  SendChangeEmailVerificationEmail,
  ChangeTutorPrivateEmail,
} from "@/api/user";

const layoutStore = useLayoutStore();
const userStore = useUserStore();
const route = useRoute();
const { emailDialog } = storeToRefs(layoutStore);

const visible = computed({
  get: () => !!emailDialog.value.dialog,
  set: (v) => {
    if (!v) layoutStore.closeEmailDialog();
    else layoutStore.openEmailDialog();
  },
});

const emailFormRef = ref(null);
const codeFormRef = ref(null);
const newEmail = ref("");
const verificationCode = ref("");
const canGet = ref(true);
const getCodeBtn = ref("Send");
const submitting = ref(false);
let timer = null;
let count = 60;

const emailModel = reactive({ newEmail: "" });
const codeModel = reactive({ verificationCode: "" });

const emailRules = {
  newEmail: [
    { required: true, message: "Required.", trigger: "blur" },
    {
      validator: (_r, v, cb) => {
        if (v && !/.+@.+/.test(v)) cb(new Error("E-mail must be valid"));
        else cb();
      },
      trigger: "blur",
    },
  ],
};
const codeRules = {
  verificationCode: [{ required: true, message: "Required.", trigger: "blur" }],
};

function clearTimer() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  canGet.value = true;
  getCodeBtn.value = "Send";
  count = 60;
}

function startCountdown() {
  if (timer) return;
  count = 60;
  canGet.value = false;
  getCodeBtn.value = `(${count})s`;
  timer = setInterval(() => {
    if (count > 0) {
      count -= 1;
      getCodeBtn.value = `(${count})s`;
    } else {
      clearTimer();
      getCodeBtn.value = "reSend";
    }
  }, 1000);
}

async function sendCode() {
  verificationCode.value = "";
  codeModel.verificationCode = "";
  codeFormRef.value?.resetFields?.();
  try {
    await emailFormRef.value?.validate?.();
  } catch {
    return;
  }
  if (newEmail.value === userStore.email) {
    ElMessage.warning("The new email is same with the old one.");
    return;
  }
  startCountdown();
  try {
    const body = await SendChangeEmailVerificationEmail(userStore.email);
    if (body && typeof body === "object" && body.Code != null && body.Code !== 0) {
      ElMessage.error(body.Msg || "Failed");
      return;
    }
    ElMessage.success(body?.Msg || "send success");
  } catch (e) {
    ElMessage.error(typeof e === "string" ? e : e?.message || "Failed");
  }
}

async function changeEmail() {
  emailModel.newEmail = newEmail.value;
  codeModel.verificationCode = verificationCode.value;
  try {
    await Promise.all([
      emailFormRef.value?.validate?.(),
      codeFormRef.value?.validate?.(),
    ]);
  } catch {
    return;
  }
  submitting.value = true;
  try {
    const body = await ChangeTutorPrivateEmail(
      userStore.email,
      verificationCode.value,
      newEmail.value,
    );
    if (body && typeof body === "object" && body.Code != null && body.Code !== 0) {
      ElMessage.error(body.Msg || "Failed");
      return;
    }
    ElMessage.success(body?.Msg || "Success");
    userStore.updateEmail(newEmail.value);
    layoutStore.closeEmailDialog();
    if (route.name === "newPersonalData" || route.path.includes("newPersonalData")) {
      setTimeout(() => window.location.reload(), 2000);
    }
  } catch (e) {
    ElMessage.error(typeof e === "string" ? e : e?.message || "Failed");
  } finally {
    submitting.value = false;
  }
}

watch(visible, (open) => {
  if (open) {
    newEmail.value = "";
    verificationCode.value = "";
    emailModel.newEmail = "";
    codeModel.verificationCode = "";
    emailFormRef.value?.resetFields?.();
    codeFormRef.value?.resetFields?.();
  } else {
    clearTimer();
  }
});

onBeforeUnmount(clearTimer);
</script>

<template>
  <el-dialog
    v-model="visible"
    title="Change E-mail"
    width="550px"
    :close-on-click-modal="false"
    align-center
  >
    <el-form
      ref="emailFormRef"
      :model="emailModel"
      :rules="emailRules"
      label-position="top"
    >
      <el-form-item label="Email" prop="newEmail">
        <el-input
          v-model="newEmail"
          @input="emailModel.newEmail = newEmail"
        />
      </el-form-item>
    </el-form>

    <el-form
      ref="codeFormRef"
      :model="codeModel"
      :rules="codeRules"
      label-position="top"
    >
      <div class="code-row">
        <el-form-item label="Vertification Code" prop="verificationCode" class="code-field">
          <el-input
            v-model="verificationCode"
            @input="codeModel.verificationCode = verificationCode"
          />
        </el-form-item>
        <button
          type="button"
          class="tutor-blue-btn send-btn"
          :disabled="!canGet"
          @click="sendCode"
        >
          {{ getCodeBtn }}
        </button>
      </div>
      <p class="change-email-box-tips">
        * No verification code has been received or the original email is not
        available. Please contact your administrator
      </p>
    </el-form>

    <template #footer>
      <div class="footer">
        <button
          type="button"
          class="tutor-red-btn"
          :disabled="submitting"
          @click="changeEmail"
        >
          Submit
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.code-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}
.code-field {
  flex: 1;
  margin-bottom: 8px;
}
.send-btn {
  height: 40px !important;
  margin-bottom: 8px;
  padding: 0 20px;
  white-space: nowrap;
  flex-shrink: 0;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.change-email-box-tips {
  color: rgba(50, 57, 122, 0.4);
  font-size: 13px;
  margin: 0 0 8px;
}
[data-theme="theme1"] .change-email-box-tips {
  color: rgba(255, 255, 255, 0.4);
}
.footer {
  text-align: center;
}
.tutor-red-btn {
  border: none;
  background: #ee344c;
  color: #fff;
  height: 36px !important;
  padding: 0 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
